import React, { FC, ReactNode, useMemo } from "react";
import { Dropdown, Checkbox, IOption, Link } from "@evergis/ui";
import { AttributeSelectorType, AttributeType } from "@evergis/api";
import { useTranslation } from "react-i18next";

import { dateOptions, timeOptions } from "./FormatSettingsOptions";
import { DateFormState } from "../hooks/useAttributeFormat";
import { updateDateFormat } from "../hooks/useUpdateAttributeFormat";
import { formatAttributeValue } from "../../../../../utils/formatAttributeValue";
import { FormSettingsContainer, Hint, SmallHintText } from "./styled";

type DateFormatSettingsProps = {
  type: AttributeType;
  formState: DateFormState;
  attributeSelectorType?: AttributeSelectorType;
  childrenBefore?: ReactNode;
  onChangeFormatValue: (option: IOption[]) => void;
  onChangeFormatTime: (option: IOption[]) => void;
  toggleSelected: (name: string) => void;
};

export const exampleDate = Date.now();

export const DateFormatSettings: FC<DateFormatSettingsProps> = ({
  type,
  formState,
  childrenBefore,
  attributeSelectorType,
  onChangeFormatValue,
  onChangeFormatTime,
  toggleSelected,
  children,
}) => {
  const { t } = useTranslation("dialogs");
  const disabledTime = useMemo(() => !formState.showTime, [formState.showTime]);

  const formattedExample = useMemo(
    () => formatAttributeValue(type, exampleDate, updateDateFormat(formState)),
    [type, exampleDate, formState],
  );

  return (
    <FormSettingsContainer height={"unset"}>
      {childrenBefore}
      <Dropdown
        label={`${t("dialogComponents.dateFormat")}:`}
        options={dateOptions}
        value={formState.format || dateOptions[1].value}
        width={"18rem"}
        zIndex={10000}
        onChange={onChangeFormatValue}
      />
      <Checkbox
        label={t("dialogComponents.showTime")}
        checked={formState.showTime}
        onChange={() => toggleSelected("showTime")}
      />
      <Checkbox
        label={t("dialogComponents.showTimeZone")}
        checked={formState.showTimeZone}
        onChange={() => toggleSelected("showTimeZone")}
        disabled={disabledTime}
      />
      <Dropdown
        label={`${t("dialogComponents.timeFormat")}:`}
        options={timeOptions}
        value={formState.timeFormat || timeOptions[0].value}
        width={"18rem"}
        zIndex={10000}
        onChange={onChangeFormatTime}
        disabled={disabledTime}
      />
      <Hint>
        <SmallHintText>{t("dialogComponents.example")}:</SmallHintText>
        {(!attributeSelectorType || attributeSelectorType === AttributeSelectorType.None) &&
          (formattedExample || exampleDate)}
        {attributeSelectorType === AttributeSelectorType.SelectFromHandBook && (
          <div>{t("recordFromDictionary", { ns: "common" })}</div>
        )}
        {attributeSelectorType === AttributeSelectorType.ViewHandBook && (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <Link>{formattedExample || exampleDate}</Link>
        )}
      </Hint>
      {children}
    </FormSettingsContainer>
  );
};
